/**
 * Environment prefix for React Query keys.
 * Prevents cross-environment cache contamination when the same browser
 * hits staging and production (both share the same QueryClient cache).
 */
export const ENV_PREFIX = process.env.NEXT_PUBLIC_API_URL || "default";

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

type EventListFilters = {
  search?: string;
  city?: string;
  cityId?: string;
  category?: string;
  date?: string;
};

type PopularEventFilters = {
  limit?: number;
  upcoming?: boolean;
  categoryId?: string;
  [key: string]: unknown;
};

type TrendingEventFilters = {
  limit?: number;
  upcoming?: boolean;
  trending?: boolean;
  categoryId?: string;
  tag?: string;
  search?: string;
  minPrice?: string;
  maxPrice?: string;
};

type RecommendedEventFilters = {
  signedIn?: boolean;
};

type PaymentFilters = {
  eventId?: string;
  status?: string;
  method?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
};

type RsvpFilters = {
  eventId?: string;
  status?: string;
  search?: string;
  page?: number;
};

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const queryKeys = {
  // ── Events (public) ─────────────────────────────────────────────────
  events: {
    all: [ENV_PREFIX, "events"] as const,
    list: (filters?: EventListFilters) =>
      [...queryKeys.events.all, "list", filters] as const,
    detail: (id: string) => [...queryKeys.events.all, "detail", id] as const,
    popular: (filters?: PopularEventFilters) =>
      [...queryKeys.events.all, "popular", filters] as const,
    trending: (filters?: TrendingEventFilters) =>
      [...queryKeys.events.all, "trending", filters] as const,
    recommended: (filters?: RecommendedEventFilters) =>
      [...queryKeys.events.all, "recommended", filters] as const,
    categories: () => [...queryKeys.events.all, "categories"] as const,
    cities: () => [...queryKeys.events.all, "cities"] as const,
    featured: () => [...queryKeys.events.all, "featured"] as const,
    featuredBanner: () => [...queryKeys.events.all, "featured", "banner"] as const,
  },

  // ── Host events (dashboard) ─────────────────────────────────────────
  hostEvents: {
    all: [ENV_PREFIX, "host-events"] as const,
    list: () => [...queryKeys.hostEvents.all, "list"] as const,
    detail: (id: string) =>
      [...queryKeys.hostEvents.all, "detail", id] as const,
    overview: (id: string) =>
      [...queryKeys.hostEvents.all, "overview", id] as const,
    analytics: (id: string) =>
      [...queryKeys.hostEvents.all, "analytics", id] as const,
    guests: (id: string) => [...queryKeys.hostEvents.all, "guests", id] as const,
    approvals: (id: string) =>
      [...queryKeys.hostEvents.all, "approvals", id] as const,
    payments: (id: string) =>
      [...queryKeys.hostEvents.all, "payments", id] as const,
    posts: (id: string) => [...queryKeys.hostEvents.all, "posts", id] as const,
  },

  // ── RSVPs ───────────────────────────────────────────────────────────
  rsvps: {
    all: [ENV_PREFIX, "rsvps"] as const,
    list: (filters?: RsvpFilters) =>
      [...queryKeys.rsvps.all, "list", filters] as const,
    detail: (id: string) => [...queryKeys.rsvps.all, "detail", id] as const,
    status: (eventId: string) =>
      [...queryKeys.rsvps.all, "status", eventId] as const,
    ticket: (id: string) => [...queryKeys.rsvps.all, "ticket", id] as const,
  },

  // ── Payments ────────────────────────────────────────────────────────
  payments: {
    all: [ENV_PREFIX, "payments"] as const,
    list: (filters?: PaymentFilters) =>
      [...queryKeys.payments.all, "list", filters] as const,
    detail: (id: string) =>
      [...queryKeys.payments.all, "detail", id] as const,
    dashboard: () => [...queryKeys.payments.all, "dashboard"] as const,
    settlements: () => [...queryKeys.payments.all, "settlements"] as const,
  },

  // ── Hosts ───────────────────────────────────────────────────────────
  hosts: {
    all: [ENV_PREFIX, "hosts"] as const,
    profile: (id: string) =>
      [...queryKeys.hosts.all, "profile", id] as const,
    dashboard: () => [...queryKeys.hosts.all, "dashboard"] as const,
  },

  // ── Users ───────────────────────────────────────────────────────────
  users: {
    all: [ENV_PREFIX, "users"] as const,
    profile: () => [...queryKeys.users.all, "profile"] as const,
    preferences: () => [...queryKeys.users.all, "preferences"] as const,
    tickets: () => [...queryKeys.users.all, "tickets"] as const,
  },

  // ── Merchant / Razorpay ─────────────────────────────────────────────
  merchant: {
    all: [ENV_PREFIX, "merchant"] as const,
    account: () => [...queryKeys.merchant.all, "account"] as const,
    onboarding: () => [...queryKeys.merchant.all, "onboarding"] as const,
    settlements: () => [...queryKeys.merchant.all, "settlements"] as const,
  },

  // ── Invites ─────────────────────────────────────────────────────────
  invites: {
    all: [ENV_PREFIX, "invites"] as const,
    event: (eventId: string) =>
      [...queryKeys.invites.all, "event", eventId] as const,
    transfers: () => [...queryKeys.invites.all, "transfers"] as const,
  },

  // ── Organizations ───────────────────────────────────────────────────
  organizations: {
    all: [ENV_PREFIX, "organizations"] as const,
    detail: (id: string) =>
      [...queryKeys.organizations.all, "detail", id] as const,
    members: (id: string) =>
      [...queryKeys.organizations.all, "members", id] as const,
  },

  // ── Notifications ───────────────────────────────────────────────────
  notifications: {
    all: [ENV_PREFIX, "notifications"] as const,
    list: () => [...queryKeys.notifications.all, "list"] as const,
    unreadCount: () =>
      [...queryKeys.notifications.all, "unread-count"] as const,
    preferences: () =>
      [...queryKeys.notifications.all, "preferences"] as const,
  },

  // ── Onboarding ──────────────────────────────────────────────────────
  onboarding: {
    guard: (userId: string) =>
      [ENV_PREFIX, "onboarding", "guard", userId] as const,
    status: () => [ENV_PREFIX, "onboarding", "status"] as const,
  },

  // ── Promotions ──────────────────────────────────────────────────────
  promotions: {
    urgencyConfig: () =>
      [ENV_PREFIX, "promotions", "urgency-config"] as const,
  },

  // ── Admin ───────────────────────────────────────────────────────────
  admin: {
    all: [ENV_PREFIX, "admin"] as const,
    events: () => [...queryKeys.admin.all, "events"] as const,
    users: () => [...queryKeys.admin.all, "users"] as const,
    cities: () => [...queryKeys.admin.all, "cities"] as const,
    featured: () => [...queryKeys.admin.all, "featured"] as const,
    flags: () => [...queryKeys.admin.all, "flags"] as const,
  },
} as const;
